import { BaseScene } from './BaseScene';
import { CoinFlipOverlay } from '../components/CoinFlipOverlay';
import type { LayoutMetrics } from '../LayoutManager';
import type { PlayerId } from '@/types';

export interface CoinFlipSceneOptions {
  getPlayer1Name: () => string;
  getPlayer2Name: () => string;
  title?: string;
  onCoinFlip: () => PlayerId;
  onComplete: () => void;
}

/**
 * Coin flip scene - shows animated coin flip to determine who goes first
 * Wraps CoinFlipOverlay as a full scene for use in the scene manager
 */
export class CoinFlipScene extends BaseScene {
  private metrics: LayoutMetrics;
  private options: CoinFlipSceneOptions;
  private coinFlipOverlay: CoinFlipOverlay | null = null;

  constructor(metrics: LayoutMetrics, options: CoinFlipSceneOptions) {
    super();
    this.metrics = metrics;
    this.options = options;
  }

  protected build(): void {
    // Empty - content built in onEnter
  }

  private createOverlay(): void {
    // Clean up existing
    if (this.coinFlipOverlay) {
      this.container.removeChild(this.coinFlipOverlay);
      this.coinFlipOverlay.destroy({ children: true });
      this.coinFlipOverlay = null;
    }

    this.coinFlipOverlay = new CoinFlipOverlay({
      width: this.metrics.width,
      height: this.metrics.height,
      player1Name: this.options.getPlayer1Name(),
      player2Name: this.options.getPlayer2Name(),
      title: this.options.title ?? 'Who Goes First?',
      onCoinFlip: this.options.onCoinFlip,
      onComplete: this.options.onComplete,
    });

    this.container.addChild(this.coinFlipOverlay);
  }

  onEnter(): void {
    super.onEnter();

    // Create overlay with current player names
    this.createOverlay();

    // Start the coin flip animation
    if (this.coinFlipOverlay) {
      this.coinFlipOverlay.start();
    }
  }

  onExit(): void {
    super.onExit();
    // Clean up overlay when leaving
    if (this.coinFlipOverlay) {
      this.container.removeChild(this.coinFlipOverlay);
      this.coinFlipOverlay.destroy({ children: true });
      this.coinFlipOverlay = null;
    }
  }

  onResize(metrics: LayoutMetrics): void {
    this.metrics = metrics;
    // Don't recreate during coin flip - it would reset the animation
  }

  /**
   * Get the result of the coin flip
   */
  getResult(): PlayerId | null {
    return this.coinFlipOverlay?.getResult() ?? null;
  }

  dispose(): void {
    if (this.coinFlipOverlay) {
      this.coinFlipOverlay.destroy({ children: true });
      this.coinFlipOverlay = null;
    }
    super.dispose();
  }
}
